import { useMemo } from 'react';
import { useRouter } from 'core/i18n/navigation';
import { buildSpacePath } from 'config/routing';
import type { BoxView } from 'models/BoxView';
import type { Box } from 'models/Box';
import type { Collection } from 'models/Collection';
import type { Memo } from 'models/Memo';
import type { Space } from 'models/Space';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import {
  requestToSelectSpace,
  requestToSelectBox,
  requestToSelectCollection,
  requestToSelectView,
} from 'xstate/actors/SpaceActor';

/**
 * The single, canonical way to navigate the `/spaces` app. Every destination:
 *   1. writes the selection synchronously (instant highlight / content switch)
 *   2. pushes the fully-specified URL — the source of truth that
 *      `useSpaceAutoSelector` reconciles the selection against.
 *
 * Doing both in one place enforces the invariant in `doc/spaces-navigation.md`:
 * deliberate navigations push their own URL, and nothing derives the URL from
 * state except the box-less landing redirect in `SpacesPage`. Getting this
 * combination wrong at individual call sites is what caused the
 * double-navigation flicker and the infinite-redirect loop we fixed.
 *
 * ⚠️ MULTI-SPACE CAVEAT: these helpers build URLs against `selectedSpace` from
 * context, i.e. they assume the target box/collection lives in the *currently
 * selected* space. That holds today because the UI only ever shows the current
 * space's boxes/collections. When we support navigating across spaces (a space
 * switcher, cross-space search results, etc.) this will produce a URL under the
 * wrong space — at that point `goToBox`/`goToMemo` must take the target space
 * explicitly (and `Box`/`Memo` should carry their `spaceId`), instead of
 * reading it from context here.
 */
export function useSpaceNavigation() {
  const router = useRouter();
  const { selectedSpace, selectedBox } = useActiveSpace();

  return useMemo(() => {
    const spaceId = selectedSpace?.id;

    return {
      // Select a space and open it (the auto-selector reconciles its boxes).
      goToSpace: (space: Space) => {
        requestToSelectSpace(space);
        router.push(buildSpacePath({ kind: 'space', spaceId: space.id }));
      },
      // Select a box and open its Timeline.
      goToBox: (box: Box) => {
        requestToSelectBox(box);
        if (spaceId) {
          router.push(
            buildSpacePath({ kind: 'timeline', spaceId, boxId: box.id }),
          );
        }
      },
      // Select a collection within the current box.
      goToCollection: (collection: Collection) => {
        requestToSelectCollection(collection);
        if (spaceId && selectedBox) {
          router.push(
            buildSpacePath({
              kind: 'collection',
              spaceId,
              boxId: selectedBox.id,
              collectionId: collection.id,
            }),
          );
        }
      },
      // Open one of the current box's views: the Timeline, the Journal
      // (optionally on a day), the Library or the Files. Leaves any
      // collection.
      goToView: (view: BoxView, day?: string) => {
        requestToSelectView(view, day);
        if (spaceId && selectedBox) {
          const boxId = selectedBox.id;
          router.push(
            buildSpacePath(
              view === 'journal'
                ? { kind: 'journal', spaceId, boxId, day }
                : { kind: view, spaceId, boxId },
            ),
          );
        }
      },
      // Jump straight to a memo's box/collection (e.g. a search result). No
      // optimistic select — the destination isn't the current selection.
      goToMemo: (memo: Memo) => {
        if (spaceId) {
          router.push(
            buildSpacePath(
              memo.collectionId
                ? {
                    kind: 'collection',
                    spaceId,
                    boxId: memo.boxId,
                    collectionId: memo.collectionId,
                  }
                : { kind: 'timeline', spaceId, boxId: memo.boxId },
            ),
          );
        }
      },
    };
  }, [router, selectedSpace?.id, selectedBox?.id]);
}
